import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookOpen, GraduationCap, FileText, BarChart3, MessageSquare, Users, Heart, TrendingUp, FileCheck, Award, Stethoscope, X } from "lucide-react";
import SkipToContent from "@/components/SkipToContent";
import Header from "@/components/Header";

const LandingPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDataProtectionOpen, setIsDataProtectionOpen] = useState(false);
  const [isEssentialCookiesOpen, setIsEssentialCookiesOpen] = useState(false);

  const handleLogin = () => {
    navigate("/login");
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setIsDialogOpen(true);
  };

  const services = [
    {
      icon: Users,
      title: "Research Training & Capacity Building",
      description: "Structured programmes to build strong methodological and analytical competence",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      details: {
        sections: [
          {
            title: "Research Methodologies",
            items: [
              "Qualitative research (interviews, focus groups, observation, thematic analysis)",
              "Quantitative research (study design, data collection, statistics)",
              "Mixed-methods research (integration strategies, reporting)"
            ]
          },
          {
            title: "Evidence Synthesis & Secondary Research",
            items: [
              "Systematic reviews",
              "Scoping reviews",
              "Meta-analysis (concepts, execution, interpretation)",
              "Certainty of evidence (GRADE)",
              "Evidence-to-Decision (EtD) frameworks",
              "Guideline development methodology"
            ]
          },
          {
            title: "Educational & Professional Development Training",
            items: [
              "Pedagogic workshops",
              "Personal effectiveness programmes",
              "Teaching and learning strategies",
              "Assessment and feedback (OSCEs, oral exams, WBA)",
              "Building safe and inclusive learning environments",
              "Medical and dental education research methods"
            ]
          },
          {
            title: "Clinical Research Training",
            items: [
              "Designing and conducting clinical trials",
              "Protocol development",
              "Ethical and regulatory navigation"
            ]
          }
        ]
      }
    },
    {
      icon: Heart,
      title: "Research Mentorship & Academic Guidance",
      description: "End-to-end mentorship for students, early-career researchers, and experienced academics",
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      details: {
        sections: [
          {
            title: "Research Journey Support",
            items: [
              "Refining research questions",
              "Methodology development",
              "Guidance for theses, dissertations, and capstone projects",
              "Structuring literature reviews",
              "Support for ethics submissions"
            ]
          },
          {
            title: "Content-Specific Guidance",
            items: [
              "Oral Medicine",
              "Oral & Maxillofacial Radiology",
              "Prosthodontics & Implantology",
              "Other medical, dental, nursing, and allied health domains"
            ]
          },
          {
            title: "Academic Writing & Presentation Coaching",
            items: [
              "Manuscript development",
              "Conference abstracts, posters, and slide decks",
              "Presentation coaching",
              "Responding to peer review",
              "Reporting standards (CONSORT, STROBE, PRISMA, COREQ)"
            ]
          },
          {
            title: "Mentoring Across Career Stages",
            items: [
              "Supporting early-career researchers entering evidence synthesis",
              "Working with experienced researchers on advanced projects",
              "Strengthening analytical, critical appraisal, and scholarly skills"
            ]
          }
        ]
      }
    },
    {
      icon: TrendingUp,
      title: "Data Analysis & Evidence Interpretation",
      description: "Expert analytical services with optional training for independent competence",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      details: {
        sections: [
          {
            title: "Quantitative Analysis",
            items: [
              "Descriptive and inferential statistics",
              "Regression models",
              "Sample size and power calculations"
            ]
          },
          {
            title: "Qualitative Analysis",
            items: [
              "Coding frameworks (thematic, content, framework analysis)",
              "High-quality interpretation and synthesis"
            ]
          },
          {
            title: "Mixed-Methods Integration",
            items: [
              "Joint displays",
              "Convergence and meta-inference techniques"
            ]
          },
          {
            title: "Meta-Analytical Expertise",
            items: [
              "Execution of meta-analysis",
              "Subgroup and sensitivity analyses",
              "Forest plot interpretation",
              "Narrative synthesis"
            ]
          }
        ]
      }
    },
    {
      icon: FileCheck,
      title: "Evidence Synthesis & Guideline Development Support",
      description: "We aim to provide high-quality systematic reviews, health technology assessments and evidence summaries to support regulatory decisions for pharmaceutical and medical device companies.",
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      details: {
        sections: [
          {
            title: "Core Services",
            items: [
              "Systematic and scoping review development (full-cycle support)",
              "Health Technology Assessment",
              "Meta-analysis planning and execution",
              "Certainty of evidence assessment using GRADE",
              "Development of guideline protocols",
              "Evidence-to-Decision (EtD) framework support",
              "Technical assistance for guideline panels",
              "Methodology review for proposals and grant applications"
            ]
          }
        ]
      }
    },
    {
      icon: Award,
      title: "Educational Excellence & Faculty Development",
      description: "Capacity-building for educators and institutions across health professions",
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
      details: {
        sections: [
          {
            title: "Core Services",
            items: [
              "Training in assessment design and evaluation",
              "Faculty development workshops",
              "Curriculum design and review",
              "Teaching enhancement and reflective practice",
              "Supervisory skills for research mentorship"
            ]
          }
        ]
      }
    },
    {
      icon: Stethoscope,
      title: "Clinical Research Advisory Services",
      description: "Specialised support for clinical research planning and implementation",
      color: "text-red-600",
      bgColor: "bg-red-50",
      details: {
        sections: [
          {
            title: "Core Services",
            items: [
              "Clinical trial design and methodology",
              "Participant recruitment and retention strategies",
              "Data integrity, monitoring, and documentation",
              "Ethical and governance compliance",
              "Preparation of trial reports and dissemination materials"
            ]
          }
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Header />

      {/* Hero Section */}
      <section className="py-8 sm:py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
            Expert Research Support for Your Academic Journey
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
            From methodological guidance to grant applications, we provide comprehensive research consultancy services to help you succeed.
          </p>
          {/* Hero Image - Accessible */}
          <div className="mb-6 sm:mb-8 max-w-5xl mx-auto">
            <figure role="img" aria-labelledby="hero-image-caption">
              <img 
                src="/images/hero.png" 
                alt="Professional research data analysis workspace showing a person analyzing statistical charts, graphs, and business intelligence reports on paper and laptop. Image text reads: FROM IDEA TO IMPACT - your research journey, strengthened" 
                className="w-full h-auto rounded-lg shadow-lg"
                loading="eager"
                aria-describedby="hero-image-description"
              />
              <figcaption id="hero-image-caption" className="sr-only">
                From Idea to Impact - Your Research Journey, Strengthened
              </figcaption>
              <div id="hero-image-description" className="sr-only">
                A professional workspace featuring business analytics and research data visualization. The image shows hands writing notes while reviewing various charts including line graphs, bar charts, and pie charts displayed on printed reports and a laptop screen. The scene represents the journey from research concept to measurable impact through data-driven analysis and expert consultancy support.
              </div>
            </figure>
          </div>

          <div className="mb-6 sm:mb-8 max-w-3xl mx-auto">
            <p className="text-sm sm:text-base lg:text-lg font-bold mb-3 sm:mb-4" style={{ color: '#00A0A4' }}>
              Global South Inclusion and Contextualisation Specialists
            </p>
            <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
              As researchers trained and experienced in the Global South, we bring deep contextual understanding 
              of the cultural, linguistic, and system-level realities that shape research in these settings. This 
              positions EviDent as a leader in designing and delivering studies that are methodologically rigorous, 
              culturally grounded, and genuinely tailored for implementation in Global South countries.
            </p>
          </div>
          {!user && (
            <Button
              data-testid="hero-login-btn"
              size="lg"
              onClick={handleLogin}
              className="bg-blue-600 hover:bg-blue-700 text-sm sm:text-base lg:text-lg px-6 py-4 sm:px-8 sm:py-6"
            >
              Get Started Now
            </Button>
          )}
        </div>
      </section>

      {/* Logo Divider Section */}
      <section className="py-8 sm:py-10 px-4">
        <div className="max-w-4xl mx-auto flex justify-center">
          <img 
            src="/images/logo.png" 
            alt="EviDent Research Consultancy" 
            className="h-32 sm:h-48 lg:h-56 w-auto"
          />
        </div>
      </section>

      {/* Services Section */}
      <section className="py-8 sm:py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-8 sm:mb-12">
            Our Services
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Card
                  key={service.title}
                  data-testid={`service-card-${service.title}`}
                  className="hover:shadow-xl transition-all duration-300 border-0 bg-white/90 backdrop-blur-sm cursor-pointer"
                  onClick={() => handleServiceClick(service)}
                >
                  <CardHeader>
                    <div className={`w-12 h-12 rounded-lg ${service.bgColor} flex items-center justify-center mb-4`}>
                      <Icon className={`h-6 w-6 ${service.color}`} />
                    </div>
                    <CardTitle className="text-lg sm:text-xl">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm sm:text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs sm:text-sm text-blue-600 font-medium">
                      Click to learn more →
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Service Details Modal */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            {selectedService && (
              <>
                <DialogHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`w-12 h-12 rounded-lg ${selectedService.bgColor} flex items-center justify-center`}>
                      {React.createElement(selectedService.icon, { className: `h-6 w-6 ${selectedService.color}` })}
                    </div>
                    <DialogTitle className="text-2xl">{selectedService.title}</DialogTitle>
                  </div>
                  <DialogDescription className="text-base">
                    {selectedService.description}
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-6 space-y-6">
                  {selectedService.details.sections.map((section) => (
                    <div key={section.title}>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">{section.title}</h4>
                      <ul className="space-y-2">
                        {section.items.map((item) => (
                          <li key={item} className="flex items-start space-x-2">
                            <span className="text-blue-600 mt-1">•</span>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </section>

      {/* Dashboard Image Section */}
      <section className="py-8 sm:py-12 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <img 
            src="/images/dashboard.png" 
            alt="EviDent Research Consultancy Dashboard" 
            className="w-full h-auto rounded-lg shadow-xl"
          />
        </div>
      </section>

      {/* Charges Section */}
      <section className="py-12 sm:py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-6 sm:mb-8">
            Service Charges
          </h3>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-6 sm:p-8 lg:p-10">
              <div className="space-y-4 text-gray-700">
                <p className="text-sm sm:text-base lg:text-lg leading-relaxed">
                  The charges for our research training and consultation services are determined by the specific 
                  requirements of each project. Fees vary depending on the type of methodological support requested 
                  (e.g., study design, methodological guidance, or critical review of study protocols), the nature 
                  of statistical analysis (parametric, non-parametric, or advanced methods such as regression or 
                  meta-analysis), and the format and scope of research training (virtual or face-to-face sessions, 
                  number of participants, and duration).
                </p>
                <p className="text-sm sm:text-base lg:text-lg leading-relaxed">
                  Charges for grant application support depend on the level of involvement required, including 
                  drafting, peer review, or methodological input. Similarly, data analysis costs are based on 
                  the complexity and type of data (e.g., sample size, missing data, or mixed data).
                </p>
                <p className="text-sm sm:text-base lg:text-lg leading-relaxed font-semibold text-blue-900">
                  Once you submit your project details, a tailored quotation and invoice will be provided based 
                  on the information received.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* General Enquiry Section */}
      <section className="py-12 sm:py-16 px-4 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
              Have Questions?
            </h3>
            <p className="text-base sm:text-lg text-gray-600">
              For any other queries or information about our services, feel free to reach out to us.
            </p>
          </div>
          <Card className="border-0 shadow-2xl bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-indigo-50 flex items-center justify-center mb-3 sm:mb-4 mx-auto">
                <MessageSquare className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-600" />
              </div>
              <CardTitle className="text-xl sm:text-2xl text-center">
                General Enquiry
              </CardTitle>
              <CardDescription className="text-center text-sm sm:text-base">
                Have questions or need more information? Send us a general enquiry and we'll get back to you.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 italic">
                For any other queries or information about our services
              </p>
              <Button
                onClick={() => navigate("/submit/general-enquiry")}
                className="bg-indigo-600 hover:bg-indigo-700 text-sm sm:text-base lg:text-lg px-6 py-4 sm:px-8 sm:py-6"
                data-testid="general-enquiry-btn"
              >
                Submit an Enquiry
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4">
        <div className="max-w-4xl mx-auto text-center bg-blue-600 rounded-2xl p-6 sm:p-8 lg:p-12 text-white">
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 sm:mb-4">
            Ready to Start Your Research Journey?
          </h3>
          <p className="text-sm sm:text-base lg:text-lg mb-6 sm:mb-8 text-blue-100">
            Join researchers worldwide who trust our expertise for their academic success.
          </p>
          {!user && (
            <Button
              data-testid="cta-login-btn"
              size="lg"
              onClick={handleLogin}
              className="bg-white text-blue-600 hover:bg-gray-100 text-sm sm:text-base lg:text-lg px-6 py-4 sm:px-8 sm:py-6"
            >
              Sign In to Get Started
            </Button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col items-center space-y-3 sm:space-y-4">
            <img 
              src="/images/logo.png" 
              alt="EviDent Research Consultancy" 
              className="h-12 sm:h-16 w-auto"
            />
            <div className="text-center space-y-1 sm:space-y-2">
              <p className="text-gray-400 text-xs sm:text-sm">
                © 2025 EviDent Research Consultancy Ltd. All rights reserved.
              </p>
              <p className="text-gray-500 text-xs sm:text-sm">
                Company Number: 15983903
              </p>
              <button
                onClick={() => setIsDataProtectionOpen(true)}
                className="text-blue-400 hover:text-blue-300 underline text-xs sm:text-sm transition-colors cursor-pointer"
              >
                Data Protection
              </button>
              <p className="text-gray-500 text-xs sm:text-sm">
                4-5, Russell Court, Palace Street, Plymouth, England, PL1 2AS
              </p>
              <button
                onClick={() => setIsEssentialCookiesOpen(true)}
                className="text-gray-400 hover:text-gray-300 underline text-xs mt-2 transition-colors cursor-pointer"
              >
                This site uses essential cookies for authentication and secure payments.
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Data Protection Modal */}
      <Dialog open={isDataProtectionOpen} onOpenChange={setIsDataProtectionOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Data Protection & Cookie Policy</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-gray-700 mt-4">
            <h3 className="text-lg font-semibold text-gray-900">Data Protection</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              We handle personal information, study data and manuscripts with strict confidentiality and in full 
              alignment with the EU and UK GDPR. We collect only what is necessary, apply pseudonymisation whenever 
              possible, secure data through robust technical and organisational measures, and retain it only for as 
              long as required. When we process data on behalf of clients, we follow their written instructions and 
              enter into appropriate data-processing terms.
            </p>
            <p className="text-sm sm:text-base leading-relaxed">
              If personal data ever needs to be transferred outside the UK or the European Economic Area, we will 
              only do so using legally approved safeguards. These may include an adequacy decision or the required 
              contractual protections, such as the EU Standard Contractual Clauses or the UK International Data 
              Transfer Agreement. Individuals can exercise their rights to access, rectification, erasure, 
              restriction, portability and objection at any time.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Cookies and Similar Technologies</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              We use cookies to enable essential functionality on our website. These cookies are strictly necessary 
              for the website to function properly and cannot be disabled.
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Essential Cookies:</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <strong>Session Management:</strong> We use a secure session cookie (session_token) to keep you 
                  logged in and protect your account. This cookie expires after 7 days of inactivity.
                </li>
                <li>
                  <strong>Authentication Services:</strong> When you use Google Sign In, PayPal, or Stripe, these 
                  services may set their own cookies necessary for secure authentication and payment processing.
                </li>
              </ul>
            </div>
            
            <p className="text-sm sm:text-base leading-relaxed">
              By using our services, you consent to the use of these essential cookies. You can manage cookies 
              through your browser settings, though disabling them may prevent you from accessing certain features 
              of our website.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Essential Cookies Privacy Policy Modal */}
      <Dialog open={isEssentialCookiesOpen} onOpenChange={setIsEssentialCookiesOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Essential Cookies Privacy Policy</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-gray-700 mt-4">
            <p className="text-sm text-gray-600">Last updated: 17 November 2025</p>
            
            <p className="text-sm sm:text-base leading-relaxed">
              EVIDENT RESEARCH CONSULTANCY LTD ("we", "us", "our") uses only essential cookies on this website. 
              Essential cookies are necessary for the website to function securely and correctly, and they cannot 
              be switched off in our systems.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">What Are Essential Cookies?</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              Essential cookies are small text files placed on your device to enable core website features such as 
              page navigation, security, loading times, session management, and accessibility. Without these cookies, 
              the website may not operate properly.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Why We Use Essential Cookies</h3>
            <p className="text-sm sm:text-base leading-relaxed">We use essential cookies to:</p>
            <ul className="list-disc pl-6 space-y-1 text-sm sm:text-base">
              <li>enable basic site functionality;</li>
              <li>maintain the security and stability of the website;</li>
              <li>remember your session or preferences needed to deliver the service;</li>
              <li>ensure pages load efficiently on your device.</li>
            </ul>
            <p className="text-sm sm:text-base leading-relaxed mt-2">
              These cookies do not collect personal data for marketing purposes, analytics, profiling, or advertising.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Your Consent</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              Because essential cookies are strictly necessary for the functioning of this website, they do not require 
              your consent under UK GDPR and the Privacy and Electronic Communications Regulations (PECR). They are 
              automatically placed when you visit the site.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">How Long Are Essential Cookies Stored?</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              Essential cookies may be stored only for the duration of your session or for a short period afterwards, 
              depending on their purpose.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Managing Cookies</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              You may be able to block or delete essential cookies through your browser settings. However, doing so 
              may cause parts of the website to stop working correctly.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Contact Us</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              If you have any questions about our use of essential cookies, please contact us at:{" "}
              <a href="mailto:sumanthkumbargerenagraj@gmail.com" className="text-blue-600 hover:text-blue-800 underline">
                sumanthkumbargerenagraj@gmail.com
              </a>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;